import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { usePublishToEmma } from '../hooks/usePublishToEmma';
import portalT from '../translations/portal';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import {
  ArrowLeft, Heart, Upload, Send, Loader, Lightbulb,
  MessageSquare, Plus, ThumbsUp, ThumbsDown, AlertTriangle,
  TrendingUp, TrendingDown, Minus,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const SOURCES = [
  { id: 'reviews', label: { en: 'Product Reviews', nl: 'Productreviews' } },
  { id: 'tickets', label: { en: 'Support Tickets', nl: 'Supporttickets' } },
  { id: 'emails', label: { en: 'Customer Emails', nl: 'Klant E-mails' } },
  { id: 'surveys', label: { en: 'Survey Responses', nl: 'Enquêteresultaten' } },
];

const CustomerPulse = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { lang } = useLanguage();
  const t = portalT[lang];
  const tp = t.toolPages.pulse;
  const fileRef = useRef(null);
  const chatEndRef = useRef(null);

  const [view, setView] = useState('start');
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [input, setInput] = useState('');
  const [feedbackText, setFeedbackText] = useState('');
  const [source, setSource] = useState('reviews');
  const [pulseData, setPulseData] = useState(null);

  const lastAssistant = (Array.isArray(messages) ? messages : []).filter(m => m.role === 'assistant').slice(-1)[0]?.content;
  usePublishToEmma('AI Customer Pulse', lastAssistant);

  const headers = { Authorization: `Bearer ${token}` };

  const uploadFeedback = async (file) => {
    if (!file) return;
    setLoading(true); setView('result');
    const fd = new FormData(); fd.append('file', file);
    try {
      const res = await axios.post(`${API}/compass/pulse/upload-feedback?language=${lang}`, fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      setSessionId(res.data.session_id);
      setPulseData(res.data.pulse_data);
      setMessages([{ role: 'user', content: `Uploaded feedback file (${res.data.rows} entries)` }, { role: 'assistant', content: res.data.analysis }]);
    } catch (e) { setMessages([{ role: 'assistant', content: (lang === 'en' ? 'Upload failed: ' : 'Upload mislukt: ') + (e.response?.data?.detail || e.message) }]); }
    finally { setLoading(false); }
  };

  const analyzeText = async () => {
    if (!((feedbackText) == null ? '' : String(feedbackText)).trim()) return;
    setLoading(true); setView('result');
    try {
      const res = await axios.post(`${API}/compass/pulse/analyze-text`, { feedback_text: feedbackText, source, language: lang }, { headers });
      setSessionId(res.data.session_id);
      setPulseData(res.data.pulse_data);
      setMessages([{ role: 'user', content: feedbackText }, { role: 'assistant', content: res.data.analysis }]);
    } catch (e) { setMessages([{ role: 'assistant', content: lang === 'en' ? 'Analysis failed. Please check your API configuration.' : 'Analyse mislukt. Controleer je API-configuratie.' }]); }
    finally { setLoading(false); }
  };

  const sendChat = async (text) => {
    if (!((text) == null ? '' : String(text)).trim() || loading || !sessionId) return;
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setInput(''); setLoading(true);
    try {
      const res = await axios.post(`${API}/compass/pulse/chat`, { message: text, session_id: sessionId, language: lang }, { headers });
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch (e) { setMessages(prev => [...prev, { role: 'assistant', content: lang === 'en' ? 'Error. Please try again.' : 'Fout. Probeer het opnieuw.' }]); }
    finally { setLoading(false); }
  };

  const startNew = () => { setView('start'); setMessages([]); setSessionId(null); setFeedbackText(''); setPulseData(null); };

  const TrendIcon = ({ dir }) => {
    if (dir === 'up') return <TrendingUp size={14} style={{ color: '#2d6a3d' }} />;
    if (dir === 'down') return <TrendingDown size={14} style={{ color: '#8b3a3a' }} />;
    return <Minus size={14} />;
  };

  if (view === 'start') {
    return (
      <div className="tool-page">
        <div className="tool-header">
          <button onClick={() => navigate('/portal/ai-tools')} className="btn-secondary"><ArrowLeft size={16} /> {t.common.aiTools}</button>
          <h1 className="heading-1"><Heart size={22} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 8 }} /> {tp.title}</h1>
        </div>
        <div className="tool-content">
          <div className="auto-start-section" data-testid="pulse-start">
            <div className="auto-input-card" data-testid="pulse-input">
              <div className="auto-input-header"><Heart size={20} /><h3 className="heading-3">{lang === 'nl' ? 'Analyseer Klantfeedback' : 'Analyze Customer Feedback'}</h3></div>
              <p className="body-small text-secondary">{lang === 'nl' ? 'Upload feedbackdata of plak reviews/tickets voor sentimentanalyse.' : 'Upload feedback data or paste reviews/tickets for sentiment analysis.'}</p>

              <div className="explorer-dropzone" style={{ margin: '1rem 0', padding: '1.5rem' }} onClick={() => fileRef.current?.click()} data-testid="pulse-dropzone">
                <Upload size={24} /><span className="body-small">{tp.uploadFeedback}</span>
                <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" style={{ display: 'none' }} onChange={(e) => uploadFeedback(e.target.files[0])} />
              </div>

              <div style={{ textAlign: 'center', margin: '0.5rem 0', color: 'var(--text-secondary)' }}>{tp.orPaste}</div>

              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
                {(Array.isArray(SOURCES) ? SOURCES : []).map(s => (
                  <button key={s.id} className={`compass-nav-item ${source === s.id ? 'active' : ''}`} onClick={() => setSource(s.id)} style={{ flex: 1, justifyContent: 'center', minWidth: 'fit-content' }}>
                    {s.label[lang] || s.label.en}
                  </button>
                ))}
              </div>

              <textarea className="form-input auto-textarea" placeholder={tp.analyzePlaceholder} value={feedbackText} onChange={(e) => setFeedbackText(e.target.value)} rows={5} data-testid="pulse-textarea" />
              <button className="btn-primary" style={{ width: '100%', marginTop: '1rem' }} onClick={analyzeText} disabled={!((feedbackText) == null ? '' : String(feedbackText)).trim()} data-testid="pulse-analyze-btn">
                <Heart size={16} /> {tp.analyzeBtn}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="compass-coach-page" data-testid="pulse-workspace">
      <aside className="compass-sidebar">
        <button onClick={() => navigate('/portal/ai-tools')} className="btn-secondary" style={{ width: '100%', justifyContent: 'center' }}><ArrowLeft size={14} /> {t.common.aiTools}</button>
        <div className="compass-role-badge" style={{ background: 'var(--accent-pink-200)' }}><Heart size={16} /><span className="body-small" style={{ fontWeight: 500 }}>{tp.title}</span></div>

        {pulseData && (
          <div className="sim-sidebar-stats" data-testid="pulse-stats">
            <div className="sim-stat-item">
              <Heart size={14} /><span className="body-small">{lang === 'nl' ? 'Sentiment' : 'Sentiment'}</span>
              <span className={`sim-rec-badge ${pulseData.overall_sentiment === 'Positive' ? 'sim-rec-go' : 'sim-rec-caution'}`}>{pulseData.overall_sentiment}</span>
            </div>
            <div className="sim-stat-item">
              <ThumbsUp size={14} /><span className="body-small">{lang === 'nl' ? 'Score' : 'Score'}</span>
              <span className="body-small" style={{ fontWeight: 600 }}>{pulseData.satisfaction_score}/100</span>
            </div>
            {pulseData.retention_risks?.length > 0 && (
              <div className="sim-stat-item" style={{ background: 'var(--accent-pink-200)' }}>
                <AlertTriangle size={14} /><span className="body-small">{lang === 'nl' ? 'Risico\'s' : 'Risks'}</span>
                <span className="body-small" style={{ fontWeight: 600 }}>{pulseData.retention_risks.length}</span>
              </div>
            )}
          </div>
        )}

        {pulseData?.trends && (
          <div className="pulse-trends" data-testid="pulse-trends">
            <span className="caption" style={{ fontWeight: 600 }}>{lang === 'nl' ? 'TRENDS' : 'TRENDS'}</span>
            {(Array.isArray(pulseData.trends) ? pulseData.trends : []).map((tr, i) => (
              <div key={i} className="sim-stat-item"><TrendIcon dir={tr.direction} /><span className="body-small">{tr.label}</span></div>
            ))}
          </div>
        )}

        <button className="compass-new-chat" onClick={startNew}><Plus size={14} /> {t.common.newAnalysis}</button>
      </aside>

      <main className="compass-main">
        <div className="compass-chat-container">
          {/* Pulse cards */}
          {pulseData && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', padding: '1.5rem 0 0' }} data-testid="pulse-cards">
              {pulseData.top_complaints?.length > 0 && (
                <div className="sim-outcome-card" style={{ borderTopColor: 'var(--accent-pink-200)' }}>
                  <span className="caption"><ThumbsDown size={12} style={{ display: 'inline', marginRight: 4 }} />{lang === 'nl' ? 'TOP KLACHTEN' : 'TOP COMPLAINTS'}</span>
                  {pulseData.top_complaints.slice(0, 3).map((c, i) => (
                    <div key={i} className="body-small"><strong>{c.percentage}%</strong> — {c.issue}</div>
                  ))}
                </div>
              )}
              {pulseData.top_praised?.length > 0 && (
                <div className="sim-outcome-card" style={{ borderTopColor: 'var(--accent-green-200)' }}>
                  <span className="caption"><ThumbsUp size={12} style={{ display: 'inline', marginRight: 4 }} />{lang === 'nl' ? 'TOP GEPREZEN' : 'TOP PRAISED'}</span>
                  {pulseData.top_praised.slice(0, 3).map((p, i) => (
                    <div key={i} className="body-small"><strong>{p.percentage}%</strong> — {p.feature}</div>
                  ))}
                </div>
              )}
              {pulseData.improvements?.length > 0 && (
                <div className="sim-outcome-card" style={{ borderTopColor: 'var(--accent-blue-200)' }}>
                  <span className="caption"><Lightbulb size={12} style={{ display: 'inline', marginRight: 4 }} />{lang === 'nl' ? 'VERBETERINGEN' : 'IMPROVEMENTS'}</span>
                  {pulseData.improvements.slice(0, 3).map((imp, i) => (
                    <div key={i} className="body-small">{imp}</div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="compass-chat-messages" data-testid="pulse-messages">
            {loading && messages.length === 0 && (
              <div className="auto-analyzing-state"><Loader className="spin" size={32} /><h3 className="heading-3">{lang === 'nl' ? 'Klantfeedback analyseren...' : 'Analyzing customer feedback...'}</h3></div>
            )}
            {(Array.isArray(messages) ? messages : []).map((msg, i) => (
              <div key={i} className={`compass-msg compass-msg-${msg.role}`}>
                {msg.role === 'assistant' ? <div className="compass-msg-content markdown-content"><ReactMarkdown>{msg.content}</ReactMarkdown></div> : <div className="compass-msg-content">{msg.content}</div>}
              </div>
            ))}
            {loading && messages.length > 0 && <div className="compass-msg compass-msg-assistant"><div className="compass-msg-content compass-typing"><Loader className="spin" size={16} /> {t.common.analyzing}</div></div>}
            <div ref={chatEndRef} />
          </div>

          {!loading && messages.length > 0 && messages.length <= 2 && (
            <div className="auto-followup-bar">
              {(lang === 'nl' ? ["Wat zijn de meest urgente problemen om op te lossen?", "Hoe kunnen we retentie verbeteren?", "Vergelijk klachten vs lof ratio"] : ["What are the most urgent issues to fix?", "How can we improve retention?", "Compare complaints vs praise ratio"]).map((s, i) => (
                <button key={i} className="compass-suggestion-btn" onClick={() => sendChat(s)}><Lightbulb size={14} /> {s}</button>
              ))}
            </div>
          )}

          <form className="compass-chat-input" onSubmit={(e) => { e.preventDefault(); sendChat(input); }}>
            <input type="text" value={input} onChange={(e) => setInput(e.target.value)} placeholder={tp.chatPlaceholder} disabled={loading} className="form-input" data-testid="pulse-chat-input" />
            <button type="submit" disabled={loading || !((input) == null ? '' : String(input)).trim()} className="btn-primary compass-send-btn"><Send size={16} /></button>
          </form>
        </div>
      </main>
    </div>
  );
};

export default CustomerPulse;
